import { RefreshCw } from 'lucide-react';
import { formatDate } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import type { ParticipantCoupon } from '../../api/participant-api';
import { useCanClaimCoupon, useParticipantCoupons } from '../../api/use-participant';
import { splitCoupons } from '../../model/coupons';
import { ActiveCouponCard, ClaimCouponCard, EmptyCoupons } from '../coupon-card';

interface CouponsTabProps {
  idPassbook: string;
  accentColor?: string;
  foregroundColor?: string;
}

function PastCoupon({ coupon }: { coupon: ParticipantCoupon }) {
  const used = coupon.status === 'USADO';
  const when = used ? coupon.usedAt : coupon.expiresAt;
  return (
    <li className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{coupon.benefit || coupon.campaignName || 'Cupom'}</p>
        <p className="truncate font-mono text-xs text-muted-foreground">{coupon.code}</p>
      </div>
      <div className="shrink-0 text-right">
        <span
          className={cn(
            'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
            used ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground',
          )}
        >
          {used ? 'Usado' : 'Expirado'}
        </span>
        {when ? <p className="mt-1 text-xs text-muted-foreground">{formatDate(when)}</p> : null}
        {used && coupon.orderNo ? <p className="text-xs text-muted-foreground">Pedido {coupon.orderNo}</p> : null}
      </div>
    </li>
  );
}

/** Cupons do participante (frete grátis etc.), vindos do serviço de cupons via BFF. */
export function CouponsTab({ idPassbook, accentColor, foregroundColor }: CouponsTabProps) {
  const { data, isPending, isError, refetch, isRefetching } = useParticipantCoupons(idPassbook);
  const canClaim = useCanClaimCoupon(idPassbook).data === true;

  if (isPending) {
    return (
      <div className="mx-auto w-full max-w-xl space-y-4">
        <Skeleton className="h-56 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
      </div>
    );
  }

  if (isError && !data) {
    return (
      <Card className="mx-auto flex w-full max-w-xl flex-col items-center gap-3 p-8 text-center">
        <p className="font-medium">Não foi possível carregar seus cupons.</p>
        <Button variant="outline" onClick={() => void refetch()} loading={isRefetching}>
          <RefreshCw className="size-4" />
          Tentar de novo
        </Button>
      </Card>
    );
  }

  const { active, past } = splitCoupons(data ?? []);

  if (!canClaim && active.length === 0 && past.length === 0) {
    return <EmptyCoupons className="mx-auto w-full max-w-xl" />;
  }

  return (
    <div className="mx-auto w-full max-w-xl space-y-4">
      {canClaim ? (
        <ClaimCouponCard idPassbook={idPassbook} accentColor={accentColor} foregroundColor={foregroundColor} />
      ) : null}
      {active.map((coupon) => (
        <ActiveCouponCard key={coupon.code} coupon={coupon} accentColor={accentColor} foregroundColor={foregroundColor} />
      ))}
      {!canClaim && active.length === 0 ? (
        <Card className="p-5 text-center text-sm text-muted-foreground">Nenhum cupom disponível agora.</Card>
      ) : null}
      {past.length > 0 ? (
        <Card className="p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Usados e expirados</p>
          <ul className="divide-y divide-border/60">
            {past.map((coupon) => (
              <PastCoupon key={coupon.code} coupon={coupon} />
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}
